import { Table, Badge, Group, ActionIcon, Tooltip, Button, Box, LoadingOverlay } from '@mantine/core';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTheatersSelector, selectTheaterLoading } from '../../../redux/selectors/theaterSelector';
import { getAllTheaters, deleteTheater } from '../../../redux/reducers/theaterSlice';
import { Theater } from '../../../models/theater.model';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { selectIsAdmin } from '../../../redux/selectors/authSelector';
import AddEditTheater from './add_edit_theater/add_edit_theater';
import ConfirmationModal from '../admin-common/confirmation/confirmation.modal';
import colors from '../../../utils/styles';

function TheaterList() {
  const dispatch = useDispatch();
  const theaters = useSelector(selectTheatersSelector);
  const loading = useSelector(selectTheaterLoading);
  const isAdmin = useSelector(selectIsAdmin);

  const [addEditModalOpened, setAddEditModalOpened] = useState(false);
  const [activeTheater, setActiveTheater] = useState<Theater | null>(null);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);

  useEffect(() => {
    dispatch(getAllTheaters());
  }, [dispatch]);

  const rows = useMemo(() => theaters?.map((theater: Theater) => (
    <Table.Tr key={theater._id}>
      <Table.Td>{theater.theater_name}</Table.Td>
      <Table.Td>{theater.location}</Table.Td>
      <Table.Td>{theater.default_max_seats}</Table.Td>
      <Table.Td>
        <Badge color={theater.default_max_seats > 0 ? 'green' : 'red'}>
          {theater.default_max_seats > 0 ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Tooltip label="Edit Theater">
            <ActionIcon size='lg' variant="outline" color="blue" onClick={() => {
              setAddEditModalOpened(true);
              setActiveTheater(theater);
            }}
              disabled={!isAdmin}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete Theater">
            <ActionIcon size='lg' variant="outline" color="red" onClick={() => {
              setConfirmationModalOpened(true);
              setActiveTheater(theater);
            }}
              disabled={!isAdmin}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  )), [theaters, isAdmin]);

  return (
    <>
      <AddEditTheater
        opened={addEditModalOpened}
        onClose={() => setAddEditModalOpened(false)}
        activeTheater={activeTheater}
      />
      <ConfirmationModal
        opened={confirmationModalOpened}
        onClose={() => setConfirmationModalOpened(false)}
        onConfirm={() => {
          dispatch(deleteTheater(activeTheater?._id as any));
        }}
        text={`Are you sure you want to delete ${activeTheater?.theater_name}?`}
      />

      <Group justify="flex-end">
        <Button onClick={() => {
          setAddEditModalOpened(true);
          setActiveTheater(null);
        }}
          disabled={!isAdmin}
        >
          Add Theater
        </Button>
      </Group>

      <Box style={{ position: 'relative' }}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "md", blur: 0.5 }}
          loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
        />
        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th>Max Seats</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Box>
    </>
  );
}

export default TheaterList;